import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api-error";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Archive, ArchiveRestore, Copy, Pause, Pencil, Play, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  arArchiveReceivable,
  arDeleteReceivable,
  arListCategories,
  arListClients,
  arListReceivables,
  arReactivateReceivable,
  arStopReceivable,
  arUnarchiveReceivable,
} from "@/lib/ar-api";
import { arRecurrenceIT } from "@/lib/ar-format";
import { EUR, fmtDate, num } from "@/lib/format";
import { SortableHeader, makeSorter } from "@/components/SortableHeader";

// Ordine logico di ricorrenza per il sort (annuale > semestrale > … > una tantum).
const RECURRENCE_RANK = {
  annual: 6,
  semi_annual: 5,
  every_4_months: 4,
  quarterly: 3,
  bi_monthly: 2,
  monthly: 1,
  one_time: 0,
};

/**
 * Shared receivable list. Phase 1: simple list with filter, edit, duplicate,
 * stop/reactivate, delete actions. Recurring vs Ordinary discriminated by
 * recurrence_type=one_time.
 */
export function ReceivableListPage({ kind }) {
  const isOrdinary = kind === "ordinary";
  const formNewPath = isOrdinary ? "/receivables/ordinary/new" : "/receivables/new";
  const formEditBase = isOrdinary ? "/receivables/ordinary" : "/receivables";

  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  // v1.60.4 — ordinamento colonne (stesso pattern di Incassi ordinari).
  // Default: decorrenza discendente (più recenti in alto).
  const [sort, setSort] = useState({ field: "start_date", dir: "desc" });
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [r, cl, ca] = await Promise.all([
        arListReceivables({ limit: 1000, include_archived: showArchived }),
        arListClients(),
        arListCategories(),
      ]);
      setItems(r || []);
      setClients(cl || []);
      setCategories(ca || []);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, [showArchived]);

  const cliMap = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.id, c])),
    [clients],
  );
  const catMap = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c])),
    [categories],
  );

  const filtered = useMemo(() => {
    let list = items.filter((r) =>
      isOrdinary ? r.recurrence_type === "one_time" : r.recurrence_type !== "one_time",
    );
    if (q) {
      const k = q.toLowerCase();
      list = list.filter(
        (r) =>
          (r.title || "").toLowerCase().includes(k) ||
          (cliMap[r.client_id]?.name || "").toLowerCase().includes(k),
      );
    }
    // v1.60.4 — sort colonnare cliccabile.
    const sorter = makeSorter(sort, (r, field) => {
      switch (field) {
        case "client":
          return (cliMap[r.client_id]?.name || "").toLowerCase();
        case "start_date":
          return r.start_date || "";
        case "title":
          return (r.title || "").toLowerCase();
        case "category":
          return (catMap[r.category_id]?.name || "").toLowerCase();
        case "recurrence":
          return RECURRENCE_RANK[r.recurrence_type] ?? -1;
        case "amount":
          return num(r.planned_amount);
        default:
          return "";
      }
    });
    return sorter ? [...list].sort(sorter) : list;
  }, [items, isOrdinary, q, cliMap, catMap, sort]);

  const [confirmDelete, setConfirmDelete] = useState(null);

  const remove = async (r) => {
    try {
      await arDeleteReceivable(r.id);
      toast.success("Eliminato.");
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Errore eliminazione"));
      setConfirmDelete(null);
    }
  };

  const stopOrReactivate = async (r) => {
    try {
      if (r.is_active) {
        await arStopReceivable(r.id);
        toast.success("Serie interrotta.");
      } else {
        await arReactivateReceivable(r.id);
        toast.success("Serie riattivata.");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Errore"));
    }
  };

  const archiveOrUnarchive = async (r) => {
    try {
      if (r.archived_at) {
        await arUnarchiveReceivable(r.id);
        toast.success("Incasso ripristinato.");
      } else {
        await arArchiveReceivable(r.id);
        toast.success("Incasso archiviato.");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Errore"));
    }
  };

  return (
    <div data-testid={isOrdinary ? "ordinary-receivables-page" : "recurring-receivables-page"}>
      <PageHeader
        title={isOrdinary ? "Incassi ordinari" : "Incassi ricorrenti"}
        subtitle={
          isOrdinary
            ? "Fatture occasionali, prestazioni una tantum, rimborsi attesi."
            : "Compensi periodici, abbonamenti consulenza, piani rateali clienti."
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" data-testid="btn-back-to-receivables">
              <Link to="/receivables">← Scadenze incassi</Link>
            </Button>
            <Button asChild data-testid="btn-new">
              <Link to={formNewPath}>+ {isOrdinary ? "Nuovo incasso ordinario" : "Nuova serie ricorrente"}</Link>
            </Button>
          </div>
        }
      />

      <div className="mb-3 flex items-center gap-2">
        <Input
          data-testid="filter-search"
          placeholder="Cerca per oggetto o cliente"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-sm"
        />
        <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-stone-600">
          <input
            type="checkbox"
            data-testid="toggle-show-archived"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          Mostra archiviati / interrotti
        </label>
        <span className="text-xs text-stone-500">
          {filtered.length} {filtered.length === 1 ? "voce" : "voci"}
        </span>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">
                <SortableHeader field="client" sort={sort} onChange={setSort} testId="sort-client">
                  Cliente
                </SortableHeader>
              </TableHead>
              <TableHead className="w-[110px]">
                <SortableHeader field="start_date" sort={sort} onChange={setSort} testId="sort-start-date">
                  Decorrenza
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader field="title" sort={sort} onChange={setSort} testId="sort-title">
                  Oggetto
                </SortableHeader>
              </TableHead>
              <TableHead className="w-[140px]">
                <SortableHeader field="category" sort={sort} onChange={setSort} testId="sort-category">
                  Categoria
                </SortableHeader>
              </TableHead>
              <TableHead className="w-[120px]">
                <SortableHeader field="recurrence" sort={sort} onChange={setSort} testId="sort-recurrence">
                  Ricorrenza
                </SortableHeader>
              </TableHead>
              <TableHead className="w-[110px] text-right">
                <SortableHeader field="amount" sort={sort} onChange={setSort} align="right" testId="sort-amount">
                  Importo
                </SortableHeader>
              </TableHead>
              <TableHead className="w-[210px]">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-12 text-center text-sm text-stone-500">
                  {loading
                    ? "Caricamento…"
                    : isOrdinary
                      ? "Nessun incasso ordinario. Crea il primo per iniziare."
                      : "Nessuna serie ricorrente. Crea la prima per iniziare."}
                </TableCell>
              </TableRow>
            )}
            {filtered.map((r) => (
              <TableRow key={r.id} data-testid={`row-receivable-${r.id}`}>
                <TableCell className="text-sm font-medium text-stone-900">
                  {cliMap[r.client_id]?.name || "—"}
                </TableCell>
                <TableCell className="font-mono text-xs tabular-nums text-stone-700">
                  {fmtDate(r.start_date)}
                </TableCell>
                <TableCell>
                  <div className="text-sm text-stone-800">{r.title}</div>
                  {r.archived_at && (
                    <span
                      data-testid={`badge-archived-${r.id}`}
                      className="mt-0.5 inline-block rounded bg-stone-100 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-stone-600 border border-stone-200"
                    >
                      Archiviata
                    </span>
                  )}
                  {!r.is_active && !r.archived_at && (
                    <div className="text-[10px] uppercase tracking-wider text-amber-700">
                      Interrotta dal {fmtDate(r.stopped_at)}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-sm text-stone-600">
                  {catMap[r.category_id]?.name || "—"}
                </TableCell>
                <TableCell className="text-xs text-stone-700">
                  {arRecurrenceIT[r.recurrence_type]}
                </TableCell>
                <TableCell className="text-right font-mono text-sm tabular-nums">
                  {EUR(num(r.planned_amount))}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`btn-edit-${r.id}`}
                      onClick={() => navigate(`${formEditBase}/${r.id}/edit`)}
                      className="h-7 px-2 text-xs"
                      title="Modifica"
                    >
                      <Pencil size={12} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`btn-duplicate-${r.id}`}
                      onClick={() => navigate(`${formNewPath}?from=${r.id}`)}
                      className="h-7 px-2 text-xs"
                      title="Duplica"
                    >
                      <Copy size={12} />
                    </Button>
                    {!isOrdinary && !r.archived_at && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`btn-stop-${r.id}`}
                        onClick={() => stopOrReactivate(r)}
                        className="h-7 px-2 text-xs"
                        title={r.is_active ? "Interrompi serie" : "Riattiva serie"}
                      >
                        {r.is_active ? <Pause size={12} /> : <Play size={12} />}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={r.archived_at ? `btn-unarchive-${r.id}` : `btn-archive-${r.id}`}
                      onClick={() => archiveOrUnarchive(r)}
                      className="h-7 px-2 text-xs"
                      title={r.archived_at ? "Ripristina" : "Archivia"}
                    >
                      {r.archived_at ? <ArchiveRestore size={12} /> : <Archive size={12} />}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`btn-delete-${r.id}`}
                      onClick={() => setConfirmDelete(r)}
                      className="h-7 px-2 text-xs text-rose-700"
                      title="Elimina definitivamente (solo se vergine)"
                    >
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="dialog-delete-receivable">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare definitivamente l'incasso?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.title ? <><strong>{confirmDelete.title}</strong>. </> : null}
              L'eliminazione è permessa solo se la registrazione è ancora{" "}
              <strong>vergine</strong>: nessun incasso registrato, nessuna
              scadenza incassata o cancellata. Altrimenti il sistema rifiuterà
              l'operazione e dovrai usare <em>Archivia</em> o{" "}
              <em>Interrompi</em>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="btn-confirm-delete-receivable"
              onClick={() => remove(confirmDelete)}
            >
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
